import React, { useEffect, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe, StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import PaymentService from "services/PaymentService";
import CheckoutForm from "./CheckoutForm";
import { useLocation, useNavigate } from "react-router-dom";
import PpvCheckoutImage from "components/skeleton/PpvCheckoutImage";
import { Helmet } from "react-helmet-async";

const stripeKey: string | undefined = process.env.REACT_APP_STRIPE_PUB_KEY;

let stripePromise: Stripe | PromiseLike<Stripe | null> | null;
if (stripeKey) {
  stripePromise = loadStripe(stripeKey);
} else {
  console.error("Stripe public key is not defined.");
}

const PayPerViewCheckout = ({ setIsActive }: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const ppv_event = location.state.ppv_event;
  const [clientSecret, setClientSecret] = useState("");

  const [isLoading, setIsLoading] = useState<any>(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState<any>(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  const getStripePaymentIntense = async () => {
    setIsLoading(true);
    setPaymentError(null);
    const data = {
      ppv_event_id: ppv_event?.id,
    };
    try {
      const response = await PaymentService.getPaymentIntense(data);
      if (!response?.clientSecret) {
        throw new Error("No client secret received from server");
      }
      setClientSecret(response.clientSecret);
    } catch (error: any) {
      console.error("Error occurred during payment intense:", error);
      setPaymentError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Unable to initialize payment. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStripePaymentIntense();
    console.log("ppv_event", ppv_event);
  }, []);

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#f30544",
      },
    },
  };

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  }, []);

  const finalServiceFee =
    parseFloat(ppv_event?.price) * (parseFloat(ppv_event?.service_fee) / 100);

  const finalDiscount =
    parseFloat(ppv_event?.price) * (parseFloat(ppv_event?.discount) / 100);

  const totalFee =
    parseFloat(ppv_event?.price) + finalServiceFee - finalDiscount;

  return (
    <>
      <Helmet>
        <title>
          Check Out -{" "}
          {ppv_event?.title ? `${ppv_event?.title}` : "AYOZAT Payperview"}
        </title>
        <meta name="description" content={ppv_event?.meta_description} />

        <meta name="keywords" content={ppv_event?.focus_keyword} />
        <meta property="og:title" content={ppv_event?.title} />
        <meta property="og:description" content={ppv_event?.meta_description} />

        <meta property="og:image" content={ppv_event?.bannerImage} />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ppv_event?.title} />
        <meta
          name="twitter:description"
          content={ppv_event?.meta_description}
        />
        <meta name="twitter:image" content={ppv_event?.bannerImage} />
        <meta name="twitter:site" content="AYOZAT Payperview" />
      </Helmet>
      <div className="mb-40 flex min-h-[90vh] items-center justify-center px-4 max-sm:mb-0 max-sm:h-auto max-sm:py-6">
        <div className="mx-auto h-auto w-full rounded-2xl border border-white/10 bg-[#0f1117] px-8 py-10 shadow-2xl max-sm:px-5 max-lg:m-0">
          <button
            className="mb-7 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl text-black transition-all duration-300 hover:scale-105 hover:bg-gray-200"
            onClick={() => {
              navigate(-1);
            }}
          >
            <HiArrowLeft className="text-black" />
          </button>

          <div className="grid grid-cols-2 gap-10 max-sm:grid-cols-1 max-lg:grid-cols-1">
            {/* Order summary */}
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[var(--red)]">
                Order Summary
              </p>
              <h1 className="mt-2 text-[22px] font-bold leading-tight text-white">
                {ppv_event?.title}
              </h1>
              <h1 className="mt-1 text-[32px] font-extrabold text-white">
                £{totalFee.toFixed(2)}
              </h1>

              <div className="mt-5 aspect-video max-h-[380px] w-full overflow-hidden rounded-2xl ring-1 ring-white/10">
                <img
                  src={ppv_event?.bannerImage}
                  alt={ppv_event?.title}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* breakdown */}
              <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="flex justify-between">
                  <span className="text-[14px] text-gray-400">Sub Total</span>
                  <span className="text-[14px] font-semibold text-gray-200">
                    £{ppv_event?.price}
                  </span>
                </div>
                <div className="mt-3 flex justify-between">
                  <span className="text-[14px] text-gray-400">Service Fee</span>
                  <span className="text-[14px] font-semibold text-gray-200">
                    {ppv_event?.service_fee}%
                  </span>
                </div>
                <div className="mt-3 flex justify-between">
                  <span className="text-[14px] text-gray-400">Discount</span>
                  <span className="text-[14px] font-semibold text-gray-200">
                    {ppv_event?.discount}%
                  </span>
                </div>
                <hr className="my-4 border-white/10" />
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-extrabold text-white">
                    Total
                  </span>
                  <span className="text-[18px] font-extrabold text-white">
                    £{totalFee.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment */}
            <div>
              <h2 className="mb-5 text-[18px] font-bold text-white">
                Payment details
              </h2>
              {isLoading && <PpvCheckoutImage />}

              {!isLoading && paymentError && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                  <p className="mb-6 text-center text-[14px] text-gray-400">
                    {paymentError}
                  </p>
                  <button
                    onClick={getStripePaymentIntense}
                    className="button-red rounded-full px-6 py-2 text-[14px] text-white transition-all duration-300 hover:scale-[1.03]"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {!isLoading && !paymentError && clientSecret && (
                <Elements options={options} stripe={stripePromise ?? null}>
                  <CheckoutForm ppv_event={ppv_event} />
                </Elements>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PayPerViewCheckout;